import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { AuthContext, IntlContext } from "context";
import { useSendContact } from "hooks/contact";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Textarea } from "components/ui/textarea";
import { CONTACT_SUBJECT_OPTIONS } from "./contactSubjects";
import {
    CONTACT_FIELD_ERROR_MESSAGE_KEYS,
    type ContactFieldErrorCode,
    type ContactFieldName,
    getContactFieldErrors,
    isContactSubjectValue,
} from "./contactFormValidation";

const EMPTY_FORM = { subject: "", email: "", body: "" };
const EMPTY_TOUCHED: Record<ContactFieldName, boolean> = { subject: false, email: false, body: false };

type FieldErrorProps = { id: string; message?: string };

const FieldError: React.FC<FieldErrorProps> = ({ id, message }) =>
    message ? (
        <p id={id} role="alert" className="text-sm text-destructive">
            {message}
        </p>
    ) : null;

const ContactForm: React.FC = () => {
    const intl = useContext(IntlContext);
    const { account } = useContext(AuthContext);
    const { mutateAsync: sendContact, isPending } = useSendContact();
    const [searchParams] = useSearchParams();
    const queriedSubject = searchParams.get("subject") ?? "";
    const initialSubject = isContactSubjectValue(queriedSubject) ? queriedSubject : "";

    const [values, setValues] = useState({ ...EMPTY_FORM, subject: initialSubject });
    const [touched, setTouched] = useState(EMPTY_TOUCHED);
    const [subjectSelectKey, setSubjectSelectKey] = useState(0);

    useEffect(() => {
        if (account?.user.email) {
            setValues((v) => ({ ...v, email: account.user.email }));
        }
    }, [account?.user.email]);

    const setValue = (field: ContactFieldName, value: string) => setValues((v) => ({ ...v, [field]: value }));

    const touchField = (field: ContactFieldName) => setTouched((t) => (t[field] ? t : { ...t, [field]: true }));

    const fieldErrors = getContactFieldErrors(values);
    const isFormValid = Object.keys(fieldErrors).length === 0;

    const errorMessage = (code: ContactFieldErrorCode | undefined) =>
        code ? intl.formatMessage(CONTACT_FIELD_ERROR_MESSAGE_KEYS[code]) : undefined;

    const fieldError = (field: ContactFieldName) => (touched[field] ? errorMessage(fieldErrors[field]) : undefined);

    const subjectError = fieldError("subject");
    const emailError = fieldError("email");
    const bodyError = fieldError("body");

    const resetForm = () => {
        setValues({ ...EMPTY_FORM, subject: initialSubject, email: account?.user.email ?? "" });
        setTouched(EMPTY_TOUCHED);
        setSubjectSelectKey((key) => key + 1);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isFormValid) {
            setTouched({ subject: true, email: true, body: true });
            return;
        }

        if (isPending) return;

        try {
            await sendContact({ subject: values.subject, email: values.email.trim(), body: values.body.trim() });
            toast.success(intl.formatMessage("contact.toast.success"));
            resetForm();
        } catch {
            toast.error(intl.formatMessage("contact.toast.error"));
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            <div className="space-y-2">
                <Label htmlFor="contact-subject">
                    {intl.formatMessage("contact.label.subject")}
                    <span className="text-destructive"> *</span>
                </Label>
                <Select
                    key={subjectSelectKey}
                    value={values.subject || undefined}
                    onValueChange={(value) => {
                        setValue("subject", value);
                        touchField("subject");
                    }}
                    onOpenChange={(open) => {
                        if (!open) touchField("subject");
                    }}
                    disabled={isPending}
                >
                    <SelectTrigger
                        id="contact-subject"
                        aria-invalid={Boolean(subjectError)}
                        aria-describedby={subjectError ? "contact-subject-error" : undefined}
                        className="h-11 rounded-xl"
                    >
                        <SelectValue placeholder={intl.formatMessage("contact.label.subject.placeholder")} />
                    </SelectTrigger>
                    <SelectContent>
                        {CONTACT_SUBJECT_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                                {intl.formatMessage(option.labelKey)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <FieldError id="contact-subject-error" message={subjectError} />
            </div>

            <div className="space-y-2">
                <Label htmlFor="contact-email">
                    {intl.formatMessage("contact.label.email")}
                    <span className="text-destructive"> *</span>
                </Label>
                <Input
                    id="contact-email"
                    type="email"
                    autoComplete="email"
                    value={values.email}
                    onChange={(e) => setValue("email", e.target.value)}
                    onBlur={() => touchField("email")}
                    placeholder={intl.formatMessage("contact.label.email")}
                    disabled={isPending}
                    aria-invalid={Boolean(emailError)}
                    aria-describedby={emailError ? "contact-email-error" : undefined}
                    className="h-11 rounded-xl"
                />
                <FieldError id="contact-email-error" message={emailError} />
            </div>

            <div className="space-y-2">
                <Label htmlFor="contact-body">
                    {intl.formatMessage("contact.label.message")}
                    <span className="text-destructive"> *</span>
                </Label>
                <Textarea
                    id="contact-body"
                    value={values.body}
                    onChange={(e) => setValue("body", e.target.value)}
                    onBlur={() => touchField("body")}
                    placeholder={intl.formatMessage("contact.label.message")}
                    disabled={isPending}
                    aria-invalid={Boolean(bodyError)}
                    aria-describedby={bodyError ? "contact-body-error" : undefined}
                    className="min-h-[140px] rounded-xl"
                />
                <FieldError id="contact-body-error" message={bodyError} />
            </div>

            <div className="flex justify-center pt-2">
                <Button
                    type="submit"
                    size="lg"
                    disabled={!isFormValid || isPending}
                    className="min-w-[180px] uppercase tracking-wide"
                >
                    {isPending && <Loader2 className="animate-spin" aria-hidden />}
                    {intl.formatMessage("generic.action.send")}
                </Button>
            </div>
        </form>
    );
};

export default ContactForm;
